import React, { useState, useEffect } from 'react';
import {
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  BookOpenIcon,
  UserGroupIcon,
  ChartBarIcon,
  PresentationChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface EducationInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: 'lesson-planning' | 'learning-objectives' | 'assessment-design' | 'classroom-management' | 'curriculum-mapping';
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const EducationInteractive: React.FC<EducationInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  
  // Tracking states
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(180); // 3 minutes for education planning
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your educational planning for best results.', {
          icon: '👩‍🏫',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    switch (exerciseType) {
      case 'lesson-planning':
        setInputs({
          subject: '',
          gradeLevel: '',
          duration: '',
          objectives: ['', '', ''],
          materials: ['', ''],
          activities: ['', '', ''],
          assessment: '',
          differentiation: ''
        });
        break;
      case 'learning-objectives':
        setInputs({
          cognitiveObjectives: ['', ''],
          affectiveObjectives: [''],
          psychomotorObjectives: [''],
          bloomsLevel: '',
          measurability: '',
          alignment: ''
        });
        break;
      case 'assessment-design':
        setInputs({
          assessmentType: '',
          rubric: {
            criteria: ['', '', ''],
            levels: ['Excellent', 'Good', 'Satisfactory', 'Needs Improvement']
          },
          questions: ['', '', ''],
          feedback: ''
        });
        break;
      case 'classroom-management':
        setInputs({
          scenario: '',
          strategy: '',
          prevention: ['', ''],
          intervention: '',
          positiveReinforcement: ['', ''],
          consequences: ''
        });
        break;
      case 'curriculum-mapping':
        setInputs({
          standards: ['', '', ''],
          sequence: ['', '', '', ''],
          assessments: ['', ''],
          resources: ['', '', ''],
          timeline: ''
        });
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes on your educational planning.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => {
      const value = inputs[field];
      return !value || (Array.isArray(value) ? value.every(v => !v) : !value.length);
    });
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'lesson-planning':
        return ['subject', 'gradeLevel', 'assessment'];
      case 'learning-objectives':
        return ['bloomsLevel', 'measurability'];
      case 'assessment-design':
        return ['assessmentType', 'feedback'];
      case 'classroom-management':
        return ['strategy', 'intervention'];
      case 'curriculum-mapping':
        return ['timeline'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'lesson-planning':
        return evaluateLessonPlanning();
      case 'learning-objectives':
        return evaluateLearningObjectives();
      case 'assessment-design':
        return evaluateAssessmentDesign();
      case 'classroom-management':
        return evaluateClassroomManagement();
      case 'curriculum-mapping':
        return evaluateCurriculumMapping();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluateLessonPlanning = () => {
    let score = 0;
    const feedback = [];

    // Subject and grade level (10 points)
    if (inputs.subject && inputs.gradeLevel) {
      score += 10;
      feedback.push('✓ Subject and grade level specified');
    }

    // Learning objectives (25 points)
    const validObjectives = inputs.objectives?.filter((obj: string) => obj.length > 20).length || 0;
    score += Math.min(25, validObjectives * 8.33);
    if (validObjectives >= 2) {
      feedback.push('✓ Clear learning objectives provided');
    }

    // Activities planning (25 points)
    const validActivities = inputs.activities?.filter((act: string) => act.length > 30).length || 0;
    score += Math.min(25, validActivities * 8.33);
    if (validActivities >= 2) {
      feedback.push('✓ Engaging activities planned');
    }

    // Assessment strategy (25 points)
    if (inputs.assessment?.length > 50) {
      score += 25;
      feedback.push('✓ Assessment strategy included');
    } else {
      feedback.push('Expand your assessment planning');
    }

    // Differentiation (15 points)
    if (inputs.differentiation?.length > 40) {
      score += 15;
      feedback.push('✓ Differentiation strategies considered');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateLearningObjectives = () => {
    let score = 0;
    const feedback = [];

    // Cognitive objectives (40 points)
    const validCognitive = inputs.cognitiveObjectives?.filter((obj: string) => obj.length > 25).length || 0;
    score += Math.min(40, validCognitive * 20);
    if (validCognitive >= 1) {
      feedback.push('✓ Cognitive objectives well-defined');
    }

    // Bloom's taxonomy alignment (30 points)
    if (inputs.bloomsLevel?.length > 20) {
      score += 30;
      feedback.push('✓ Bloom\'s taxonomy level identified');
    }

    // Measurability (30 points)
    if (inputs.measurability?.length > 40) {
      score += 30;
      feedback.push('✓ Objectives are measurable');
    } else {
      feedback.push('Make objectives more specific and measurable');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateAssessmentDesign = () => {
    let score = 0;
    const feedback = [];

    // Assessment type and structure (25 points)
    if (inputs.assessmentType) {
      score += 25;
      feedback.push('✓ Assessment type selected');
    }

    // Rubric criteria (35 points)
    const validCriteria = inputs.rubric?.criteria?.filter((c: string) => c.length > 15).length || 0;
    score += Math.min(35, validCriteria * 11.67);
    if (validCriteria >= 2) {
      feedback.push('✓ Rubric criteria developed');
    }

    // Assessment questions (25 points)
    const validQuestions = inputs.questions?.filter((q: string) => q.length > 20).length || 0;
    score += Math.min(25, validQuestions * 8.33);

    // Feedback mechanism (15 points)
    if (inputs.feedback?.length > 30) {
      score += 15;
      feedback.push('✓ Feedback strategy included');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateClassroomManagement = () => {
    let score = 0;
    const feedback = [];

    // Management strategy (35 points)
    if (inputs.strategy?.length > 60) {
      score += 35;
      feedback.push('✓ Clear management strategy');
    } else {
      feedback.push('Provide more detailed management strategy');
    }

    // Prevention techniques (25 points)
    const validPrevention = inputs.prevention?.filter((p: string) => p.length > 20).length || 0;
    score += Math.min(25, validPrevention * 12.5);

    // Intervention approach (25 points)
    if (inputs.intervention?.length > 40) {
      score += 25;
      feedback.push('✓ Intervention approach outlined');
    }

    // Positive reinforcement (15 points)
    const validReinforcement = inputs.positiveReinforcement?.filter((r: string) => r.length > 15).length || 0;
    score += Math.min(15, validReinforcement * 7.5);

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateCurriculumMapping = () => {
    let score = 0;
    const feedback = [];

    // Standards alignment (30 points)
    const validStandards = inputs.standards?.filter((s: string) => s.length > 20).length || 0;
    score += Math.min(30, validStandards * 10);
    if (validStandards >= 2) {
      feedback.push('✓ Standards clearly identified');
    }

    // Learning sequence (30 points)
    const validSequence = inputs.sequence?.filter((s: string) => s.length > 15).length || 0;
    score += Math.min(30, validSequence * 7.5);

    // Assessment alignment (25 points)
    const validAssessments = inputs.assessments?.filter((a: string) => a.length > 20).length || 0;
    score += Math.min(25, validAssessments * 12.5);

    // Timeline planning (15 points)
    if (inputs.timeline?.length > 30) {
      score += 15;
      feedback.push('✓ Timeline provided');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent educational planning! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'lesson-planning': return <BookOpenIcon className="h-6 w-6" />;
      case 'learning-objectives': return <ClipboardDocumentCheckIcon className="h-6 w-6" />;
      case 'assessment-design': return <ChartBarIcon className="h-6 w-6" />;
      case 'classroom-management': return <UserGroupIcon className="h-6 w-6" />;
      case 'curriculum-mapping': return <PresentationChartBarIcon className="h-6 w-6" />;
      default: return <AcademicCapIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'lesson-planning':
        return (
          <div className="space-y-6">
            {scenario?.lessonContext && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Lesson Context:</h4>
                <div className="text-sm text-gray-700">{scenario.lessonContext}</div>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={inputs.subject || ''}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Mathematics"
                  disabled={isSubmitted}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grade Level *
                </label>
                <input
                  type="text"
                  value={inputs.gradeLevel || ''}
                  onChange={(e) => handleInputChange('gradeLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 5th Grade"
                  disabled={isSubmitted}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration
                </label>
                <input
                  type="text"
                  value={inputs.duration || ''}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 45 minutes"
                  disabled={isSubmitted}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Learning Objectives
              </label>
              {[0, 1, 2].map((index) => (
                <textarea
                  key={index}
                  value={inputs.objectives?.[index] || ''}
                  onChange={(e) => {
                    const newObjectives = [...(inputs.objectives || ['', '', ''])];
                    newObjectives[index] = e.target.value;
                    handleInputChange('objectives', newObjectives);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  rows={2}
                  placeholder={`Objective ${index + 1}: Students will be able to...`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Learning Activities
              </label>
              {[0, 1, 2].map((index) => (
                <textarea
                  key={index}
                  value={inputs.activities?.[index] || ''}
                  onChange={(e) => {
                    const newActivities = [...(inputs.activities || ['', '', ''])];
                    newActivities[index] = e.target.value;
                    handleInputChange('activities', newActivities);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  rows={3}
                  placeholder={`Activity ${index + 1}: Describe the learning activity and student engagement`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assessment Strategy *
              </label>
              <textarea
                value={inputs.assessment || ''}
                onChange={(e) => handleInputChange('assessment', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="How will you assess student learning and understanding?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Differentiation Strategies
              </label>
              <textarea
                value={inputs.differentiation || ''}
                onChange={(e) => handleInputChange('differentiation', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="How will you accommodate different learning styles and abilities?"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'learning-objectives':
        return (
          <div className="space-y-6">
            {scenario?.context && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Learning Context:</h4>
                <div className="text-sm text-gray-700">{scenario.context}</div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cognitive Learning Objectives
              </label>
              {[0, 1].map((index) => (
                <textarea
                  key={index}
                  value={inputs.cognitiveObjectives?.[index] || ''}
                  onChange={(e) => {
                    const newObjectives = [...(inputs.cognitiveObjectives || ['', ''])];
                    newObjectives[index] = e.target.value;
                    handleInputChange('cognitiveObjectives', newObjectives);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  rows={2}
                  placeholder={`Cognitive Objective ${index + 1}: Students will be able to analyze, evaluate, create...`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Affective Learning Objectives
              </label>
              <textarea
                value={inputs.affectiveObjectives?.[0] || ''}
                onChange={(e) => {
                  const newObjectives = [e.target.value];
                  handleInputChange('affectiveObjectives', newObjectives);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={2}
                placeholder="Affective Objective: Students will appreciate, value, demonstrate attitude..."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Psychomotor Learning Objectives
              </label>
              <textarea
                value={inputs.psychomotorObjectives?.[0] || ''}
                onChange={(e) => {
                  const newObjectives = [e.target.value];
                  handleInputChange('psychomotorObjectives', newObjectives);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={2}
                placeholder="Psychomotor Objective: Students will demonstrate, perform, execute..."
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bloom's Taxonomy Level Analysis *
              </label>
              <textarea
                value={inputs.bloomsLevel || ''}
                onChange={(e) => handleInputChange('bloomsLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={2}
                placeholder="Identify the Bloom's taxonomy levels (Remember, Understand, Apply, Analyze, Evaluate, Create) for your objectives"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurability Assessment *
              </label>
              <textarea
                value={inputs.measurability || ''}
                onChange={(e) => handleInputChange('measurability', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="Explain how these objectives are measurable and observable. What evidence will demonstrate achievement?"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standards Alignment
              </label>
              <textarea
                value={inputs.alignment || ''}
                onChange={(e) => handleInputChange('alignment', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={2}
                placeholder="How do these objectives align with curriculum standards and learning goals?"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'assessment-design':
        return (
          <div className="space-y-6">
            {scenario?.assessmentContext && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Assessment Context:</h4>
                <div className="text-sm text-gray-700">{scenario.assessmentContext}</div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assessment Type *
              </label>
              <select
                value={inputs.assessmentType || ''}
                onChange={(e) => handleInputChange('assessmentType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                disabled={isSubmitted}
              >
                <option value="">Select assessment type...</option>
                <option value="formative">Formative Assessment</option>
                <option value="summative">Summative Assessment</option>
                <option value="diagnostic">Diagnostic Assessment</option>
                <option value="authentic">Authentic Assessment</option>
                <option value="performance">Performance Assessment</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rubric Criteria
              </label>
              {[0, 1, 2].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.rubric?.criteria?.[index] || ''}
                  onChange={(e) => {
                    const newRubric = { ...inputs.rubric, criteria: [...(inputs.rubric?.criteria || ['', '', ''])] };
                    newRubric.criteria[index] = e.target.value;
                    handleInputChange('rubric', newRubric);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  placeholder={`Assessment Criteria ${index + 1}: e.g., Content Knowledge, Critical Thinking, Communication`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assessment Questions/Tasks
              </label>
              {[0, 1, 2].map((index) => (
                <textarea
                  key={index}
                  value={inputs.questions?.[index] || ''}
                  onChange={(e) => {
                    const newQuestions = [...(inputs.questions || ['', '', ''])];
                    newQuestions[index] = e.target.value;
                    handleInputChange('questions', newQuestions);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  rows={2}
                  placeholder={`Question/Task ${index + 1}: Design an assessment item that measures student learning`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feedback Strategy *
              </label>
              <textarea
                value={inputs.feedback || ''}
                onChange={(e) => handleInputChange('feedback', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="Describe how you will provide meaningful feedback to students based on this assessment"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'classroom-management':
        return (
          <div className="space-y-6">
            {scenario?.managementScenario && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Classroom Scenario:</h4>
                <div className="text-sm text-gray-700">{scenario.managementScenario}</div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scenario Analysis
              </label>
              <textarea
                value={inputs.scenario || ''}
                onChange={(e) => handleInputChange('scenario', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={2}
                placeholder="Describe what you observe in this classroom management situation"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Management Strategy *
              </label>
              <textarea
                value={inputs.strategy || ''}
                onChange={(e) => handleInputChange('strategy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="Describe your overall classroom management strategy and approach"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prevention Techniques
              </label>
              {[0, 1].map((index) => (
                <textarea
                  key={index}
                  value={inputs.prevention?.[index] || ''}
                  onChange={(e) => {
                    const newPrevention = [...(inputs.prevention || ['', ''])];
                    newPrevention[index] = e.target.value;
                    handleInputChange('prevention', newPrevention);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  rows={2}
                  placeholder={`Prevention Strategy ${index + 1}: How can you prevent this issue from occurring?`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intervention Approach *
              </label>
              <textarea
                value={inputs.intervention || ''}
                onChange={(e) => handleInputChange('intervention', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="Describe your intervention strategy when issues arise in the classroom"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Positive Reinforcement Strategies
              </label>
              {[0, 1].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.positiveReinforcement?.[index] || ''}
                  onChange={(e) => {
                    const newReinforcement = [...(inputs.positiveReinforcement || ['', ''])];
                    newReinforcement[index] = e.target.value;
                    handleInputChange('positiveReinforcement', newReinforcement);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  placeholder={`Positive Reinforcement ${index + 1}: e.g., praise, rewards, recognition`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consequences Framework
              </label>
              <textarea
                value={inputs.consequences || ''}
                onChange={(e) => handleInputChange('consequences', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={2}
                placeholder="Describe your approach to logical consequences and disciplinary measures"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      case 'curriculum-mapping':
        return (
          <div className="space-y-6">
            {scenario?.curriculumContext && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Curriculum Context:</h4>
                <div className="text-sm text-gray-700">{scenario.curriculumContext}</div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standards Alignment
              </label>
              {[0, 1, 2].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.standards?.[index] || ''}
                  onChange={(e) => {
                    const newStandards = [...(inputs.standards || ['', '', ''])];
                    newStandards[index] = e.target.value;
                    handleInputChange('standards', newStandards);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  placeholder={`Standard ${index + 1}: List relevant curriculum standards (e.g., Common Core, State Standards)`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Learning Sequence
              </label>
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.sequence?.[index] || ''}
                  onChange={(e) => {
                    const newSequence = [...(inputs.sequence || ['', '', '', ''])];
                    newSequence[index] = e.target.value;
                    handleInputChange('sequence', newSequence);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  placeholder={`Learning Unit ${index + 1}: Sequence of learning units/topics`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assessment Alignment
              </label>
              {[0, 1].map((index) => (
                <textarea
                  key={index}
                  value={inputs.assessments?.[index] || ''}
                  onChange={(e) => {
                    const newAssessments = [...(inputs.assessments || ['', ''])];
                    newAssessments[index] = e.target.value;
                    handleInputChange('assessments', newAssessments);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  rows={2}
                  placeholder={`Assessment ${index + 1}: How will you assess learning for each unit?`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Learning Resources
              </label>
              {[0, 1, 2].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.resources?.[index] || ''}
                  onChange={(e) => {
                    const newResources = [...(inputs.resources || ['', '', ''])];
                    newResources[index] = e.target.value;
                    handleInputChange('resources', newResources);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                  placeholder={`Resource ${index + 1}: Textbooks, materials, technology tools, etc.`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timeline Planning *
              </label>
              <textarea
                value={inputs.timeline || ''}
                onChange={(e) => handleInputChange('timeline', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="Provide a timeline for curriculum implementation (weeks, months, quarters)"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="mb-4">
              {getExerciseIcon()}
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Education Exercise
            </h4>
            <p className="text-gray-600 mb-6">
              This education exercise is being developed with specialized pedagogical tools.
            </p>
            
            <button
              onClick={() => onComplete(true, 85)}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Complete Exercise (Temporary)
            </button>
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-green-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum planning time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-green-600 hover:text-green-700 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              {exerciseType === 'lesson-planning' ? 'Submit Lesson Plan' : 
               exerciseType === 'learning-objectives' ? 'Submit Learning Objectives' :
               exerciseType === 'assessment-design' ? 'Submit Assessment Design' :
               exerciseType === 'classroom-management' ? 'Submit Management Strategy' :
               'Submit Curriculum Map'}
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EducationInteractive;